import { css, html, LitElement, nothing } from 'lit';
import { fontAwesome } from '../css/icons/font-awesome.js';

class OmegaTooltip extends LitElement {
    static get properties() {
        return {
            clickable: { type: Boolean, reflect: true },
            direction: String,
            icon: String,
            light: { type: Boolean, reflect: true },
            message: String,
            show: { type: Boolean, reflect: true },
            class: String,
            iconInheritColor: { type: Boolean, reflect: true },
        };
    }

    constructor() {
        super();
        this.class = 'omega-tooltip';
        this.show = false;
        this._boundListenForDocumentClicks = this._listenForDocumentClicks.bind(this);
        this._boundListenForKeydown = this._listenForKeydown.bind(this);
    }

    static get meta() {
        return {
            docUrl: 'https://banno.github.io/treasury-management/?path=/docs/components-toggle--toggle',
        };
    }

    firstUpdated() {
        this.tooltipBody = this.shadowRoot.querySelector('.omega-tooltip');
        this._setDimensions();
        this.relativePosition = this.getBoundingClientRect();
        this._setTooltipClass();
        if (this.clickable) {
            this.addEventListener('click', this._showTooltip);
            this.boundDismiss = this._hideTooltip.bind(this);
        }
    }

    _toggleShow() {
        if (this.clickable) {
            this.show = !this.show;
        }

        this._addOrRemoveListeners(this.show);
    }

    _addOrRemoveListeners(show) {
        if (show) {
            this.focus();
            document.addEventListener('keydown', this._boundListenForKeydown);
            document.addEventListener('click', this._boundListenForDocumentClicks);
        } else {
            document.removeEventListener('keydown', this._boundListenForKeydown);
            document.removeEventListener('click', this._boundListenForDocumentClicks);
        }
    }

    _listenForDocumentClicks(e) {
        const shouldClose = !(e.composedPath() || []).includes(this);

        if (shouldClose) {
            e.preventDefault();
            this._hideTooltip();
        }
    }

    _listenForKeydown(e) {
        if (e.keyCode === 27) {
            this._hideTooltip();
        }
    }

    _showTooltip() {
        if (this.clickable) {
            this.show = true;
            this._addOrRemoveListeners(this.show);
            this.removeEventListener('click', this._showTooltip);
        }
    }

    _hideTooltip() {
        if (this.clickable) {
            this.show = false;
            window.requestAnimationFrame(() => {
                this.addEventListener('click', this._showTooltip);
            });
        }
    }

    _setDimensions() {
        this.height =
            parseInt(window.getComputedStyle(this.tooltipBody).height, 10) +
            parseInt(window.getComputedStyle(this.tooltipBody).paddingTop, 10) +
            parseInt(window.getComputedStyle(this.tooltipBody).paddingBottom, 10);
        this.width =
            parseInt(window.getComputedStyle(this.tooltipBody).width, 10) +
            parseInt(window.getComputedStyle(this.tooltipBody).paddingRight, 10) +
            parseInt(window.getComputedStyle(this.tooltipBody).paddingLeft, 10);
    }

    _setTooltipClass() {
        const direction = this._setTooltipDirection();
        const brightness = this.light ? 'tooltip-light' : '';
        this.class = `omega-tooltip ${brightness} ${direction}`;
    }

    _setTooltipDirection() {
        if (!this.direction) return this._calcTooltipDirection();
        if (this.direction.trim().length === 0) return this._calcTooltipDirection();
        return `tooltip-${this.direction}`;
    }

    _calcTooltipDirection() {
        if (this.height > this.relativePosition.y) return 'tooltip-bottom';
        if (this.width / 2 > this.relativePosition.x) return 'tooltip-right';
        if (this.width > window.innerWidth - this.relativePosition.x) return 'tooltip-left';
        return 'tooltip-top';
    }

    renderIcon() {
        if (!this.icon) return nothing;

        const inheritColorClass = `${this.iconInheritColor ? 'inherit-color' : ''}`;

        return html`<i
            class="omega-tooltip-icon fa fa-${this.icon} ${inheritColorClass}"
            @click=${this._toggleShow}
            @keypress=${e => {
                if (e.key === 'Enter') {
                    this._toggleShow();
                }
            }}
        ></i>`;
    }

    renderTooltipContent() {
        if (this.message) return html`${this.message}`;
        if (!this.clickable) {
            return html`<div>
                <slot name="content"></slot>
            </div> `;
        }
        return html`<div>
            <button
                id="tooltip-close-button"
                class="omega-tooltip-close-button"
                @click=${this.boundDismiss}
            >
                <i class="fa fa-times omega-tooltip-close" role="button" aria-label="Close"></i>
            </button>
            <slot name="content"></slot>
        </div>`;
    }

    renderTooltipTrigger() {
        if (this.clickable) {
            return html`<button id="tooltip-trigger" @click=${this._toggleShow}>
                <slot></slot>
            </button>`;
        }
        return html`<div id="tooltip-trigger">
            <slot></slot>
        </div>`;
    }

    render() {
        return html`<span>
            ${this.renderIcon()} ${this.renderTooltipTrigger()}
            <div class=${this.class}>${this.renderTooltipContent()}</div>
        </span>`;
    }

    static get styles() {
        return [
            fontAwesome,
            css`
                .clickable {
                    cursor: pointer;
                }
                :host {
                    --omega-tooltip-transition: all 0.1s cubic-bezier(0.84, -0.18, 0.65, 1.13);
                    --omega-tooltip-background-color: var(--omega-secondary);
                    display: block;
                }
                :host([light]) {
                    --omega-tooltip-background-color: var(--omega-white);
                }
                :host([icon]) {
                    display: inline-block;
                }

                .omega-tooltip-icon {
                    font-size: 16px;
                    color: var(--omega-secondary-lighten-100);
                }

                span {
                    display: var(--tooltip-span-display, flex);
                    align-items: center;
                    justify-content: center;
                    position: relative;
                    white-space: nowrap;
                    min-width: 25px;
                }

                #tooltip-trigger {
                    background: none;
                    border: none;
                    margin: 0;
                    padding: 0;
                }

                .omega-tooltip.tooltip-light {
                    --omega-tooltip-background-color: var(--omega-white);
                    color: var(--omega-text-default);
                    border-radius: 7px;
                    border: 1px solid var(--omega-primary-lighten-200);
                    filter: drop-shadow(0 0 5px var(--omega-secondary-lighten-200));
                }

                .omega-tooltip {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                    text-align: left;
                    white-space: normal;
                }

                .omega-tooltip:focus-within {
                    outline: none;
                }

                .omega-tooltip::before,
                .omega-tooltip {
                    opacity: 0;
                    position: absolute;
                    z-index: -100;
                }

                .omega-tooltip::before {
                    border-style: solid;
                    border-width: 16px 12px 0 12px;
                    border-color: var(--omega-tooltip-background-color) transparent transparent
                        transparent;
                    content: '';
                    transition: var(--tooltip-transition);
                    bottom: -8px;
                }

                .omega-tooltip.tooltip-bottom-right::before,
                .omega-tooltip.tooltip-bottom-left::before,
                .omega-tooltip.tooltip-bottom::before {
                    border-width: 0 12px 16px 12px;
                    border-color: transparent transparent var(--omega-tooltip-background-color)
                        transparent;
                    top: -8px;
                    bottom: auto;
                }

                .omega-tooltip.tooltip-top-right::before,
                .omega-tooltip.tooltip-bottom-right::before {
                    left: 3px;
                }
                .omega-tooltip.tooltip-top-left::before,
                .omega-tooltip.tooltip-bottom-left::before {
                    right: 3px;
                }

                .omega-tooltip.tooltip-right-bottom::before,
                .omega-tooltip.tooltip-right-top::before,
                .omega-tooltip.tooltip-right::before {
                    border-width: 12px 16px 12px 0;
                    border-color: transparent var(--omega-tooltip-background-color) transparent
                        transparent;
                    left: -8px;
                    bottom: auto;
                }

                .omega-tooltip.tooltip-left-bottom::before,
                .omega-tooltip.tooltip-left-top::before,
                .omega-tooltip.tooltip-left::before {
                    border-width: 12px 0 12px 16px;
                    border-color: transparent transparent transparent
                        var(--omega-tooltip-background-color);
                    right: -8px;
                    bottom: auto;
                }

                .omega-tooltip.tooltip-right-top::before,
                .omega-tooltip.tooltip-left-top::before {
                    top: var(--omega-tooltip-indicator-top, 45px);
                }

                .omega-tooltip.tooltip-right-bottom::before,
                .omega-tooltip.tooltip-left-bottom::before {
                    bottom: 45px;
                    top: auto;
                }

                .omega-tooltip {
                    background: var(--omega-tooltip-background-color);
                    border-radius: 0.25em;
                    bottom: calc(100% + 12px);
                    color: var(--omega-info-lighten-300);
                    font-size: 12px;
                    padding: 16px;
                    transition: var(--tooltip-transition);
                    width: max-content;
                    visibility: hidden;
                }

                .omega-tooltip.tooltip-bottom {
                    top: calc(100% + 12px);
                    bottom: auto;
                }

                .omega-tooltip.tooltip-right-top,
                .omega-tooltip.tooltip-right-bottom,
                .omega-tooltip.tooltip-right {
                    left: calc(100% + 12px);
                    bottom: auto;
                }

                .omega-tooltip.tooltip-left-bottom,
                .omega-tooltip.tooltip-left-top,
                .omega-tooltip.tooltip-left {
                    right: calc(100% + 12px);
                    bottom: auto;
                }

                .omega-tooltip.tooltip-right-top,
                .omega-tooltip.tooltip-left-top {
                    top: var(--omega-tooltip-left-top-top, -50px);
                }

                .omega-tooltip.tooltip-right-bottom,
                .omega-tooltip.tooltip-left-bottom {
                    bottom: -50px;
                    top: auto;
                }

                .omega-tooltip.tooltip-bottom-right {
                    left: 0;
                    top: calc(100% + 12px);
                    bottom: auto;
                }

                .omega-tooltip.tooltip-top-right {
                    left: 0;
                }

                .omega-tooltip.tooltip-top-left {
                    right: 0;
                }

                .omega-tooltip.tooltip-bottom-left {
                    right: 0;
                    top: calc(100% + 12px);
                    bottom: auto;
                }

                .omega-tooltip-close-button {
                    position: absolute;
                    right: 10px;
                    top: 7px;
                    background: none;
                    border: none;
                    padding: 0;
                }
                .omega-tooltip-close {
                    font-weight: normal;
                    color: var(--omega-text-tertiary);
                    cursor: pointer;
                    font-size: var(--omega-label);
                }

                :host([show]) .omega-tooltip::before,
                :host([show]) .omega-tooltip,
                :host(:not([clickable]):hover) .omega-tooltip::before,
                :host(:not([clickable]):focus-within) .omega-tooltip::before,
                :host(:not([clickable]):hover) .omega-tooltip,
                :host(:not([clickable]):focus-within) .omega-tooltip {
                    opacity: 1;
                    z-index: 100;
                    visibility: visible;
                }
                .inherit-color {
                    color: inherit;
                }
            `,
        ];
    }
}
customElements.define('omega-tooltip', OmegaTooltip);
export default OmegaTooltip;
